import { styled, alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

const CalendarStyle = styled('div')(({ theme }) => ({
  width: 'calc(100% + 2px)',
  marginLeft: -1,
  marginBottom: -1,
  '& .fc': {
    '--fc-list-event-dot-width': '8px',
    '--fc-event-border-color': theme.palette.info.light,
    '--fc-now-indicator-color': theme.palette.error.main,
    '--fc-today-bg-color': alpha('#7C3AED', 0.06),
    '--fc-page-bg-color': 'transparent',
    '--fc-neutral-bg-color': alpha('#7C3AED', 0.02),
    '--fc-list-event-hover-bg-color': alpha('#7C3AED', 0.06),
    '--fc-event-bg-color': alpha('#7C3AED', 0.08),
    '--fc-highlight-color': alpha('#7C3AED', 0.08),
    '--fc-border-color': alpha('#7C3AED', 0.15),
  },

  '& .fc .fc-license-message': { display: 'none' },
  '& .fc a': { color: theme.palette.text.primary },

  // Table Head
  '& .fc .fc-col-header': {
    boxShadow: `inset 0 -1px 0 ${alpha('#7C3AED', 0.1)}`,
    backgroundColor: alpha('#7C3AED', 0.02),
    '& th': {
      borderColor: alpha('#7C3AED', 0.06),
    },
    '& .fc-col-header-cell-cushion': {
      ...theme.typography.subtitle2,
      padding: '13px 0',
      color: '#6D28D9',
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
  },

  // Event
  '& .fc .fc-timegrid-slot': {
    height: 100,
  },

  '& .fc .fc-event': {
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[4],
    },
  },

  '& .fc .fc-event .fc-event-main': {
    padding: '4px 6px',
    border: 'none',
    borderRadius: 6,
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    transition: theme.transitions.create('filter'),
    '&:hover': { filter: 'brightness(0.95)' },
    '&:before,&:after': {
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      content: "''",
      borderRadius: 6,
      position: 'absolute',
      boxSizing: 'border-box',
    },
    '&:before': {
      zIndex: 8,
      opacity: 0.25,
      border: 'solid 1px currentColor',
    },
    '&:after': {
      zIndex: 7,
      opacity: 0.18,
      backgroundColor: 'currentColor',
    },
  },

  '& .fc .fc-event .fc-event-main-frame': {
    fontSize: 13,
    lineHeight: '20px',
  },

  '& .fc .fc-daygrid-event .fc-event-title': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },

  '& .fc .fc-event .fc-event-time': {
    padding: 0,
    overflow: 'unset',
    fontWeight: theme.typography.fontWeightBold,
  },

  // Popover
  '& .fc .fc-popover': {
    border: 0,
    overflow: 'hidden',
    boxShadow: theme.shadows[8],
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
  },

  '& .fc .fc-popover-header': {
    ...theme.typography.subtitle2,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderBottom: `solid 1px ${theme.palette.divider}`,
    color: theme.palette.text.primary,
  },

  '& .fc .fc-popover-close': {
    opacity: 0.48,
    transition: theme.transitions.create('opacity'),
    '&:hover': { opacity: 1 },
  },

  '& .fc .fc-more-popover .fc-popover-body': {
    padding: theme.spacing(1.5),
  },

  '& .fc .fc-popover-body': {
    '& .fc-daygrid-event.fc-event-start, & .fc-daygrid-event.fc-event-end': {
      margin: '2px 0',
    },
  },

  // Month View
  '& .fc .fc-day-other .fc-daygrid-day-top': {
    opacity: 1,
    '& .fc-daygrid-day-number': {
      color: theme.palette.text.disabled,
    },
  },

  '& .fc .fc-day-other': {
    position: 'relative',
    backgroundColor: alpha('#7C3AED', 0.02),
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: `repeating-linear-gradient(45deg, transparent, transparent 2px, ${alpha('#7C3AED', 0.02)} 5px, ${alpha('#7C3AED', 0.04)} 20px)`,
      pointerEvents: 'none',
    },
  },

  '& .fc .fc-daygrid-day-number': {
    ...theme.typography.body2,
    padding: theme.spacing(1, 1, 0),
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },

  '& .fc .fc-day-today .fc-daygrid-day-number': {
    color: '#7C3AED',
    fontWeight: 700,
  },

  '& .fc .fc-daygrid-event': {
    marginTop: 4,
  },

  '& .fc .fc-daygrid-event.fc-event-start, & .fc .fc-daygrid-event.fc-event-end': {
    marginLeft: 4,
    marginRight: 4,
  },

  '& .fc .fc-daygrid-more-link': {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
  },

  // Week & Day View
  '& .fc .fc-timegrid-axis-cushion': {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
  },

  '& .fc .fc-timegrid-slot-label-cushion': {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
  },

  // Agenda View
  '& .fc-direction-ltr .fc-list-day-text, .fc-direction-rtl .fc-list-day-side-text, .fc-direction-ltr .fc-list-day-side-text, .fc-direction-rtl .fc-list-day-text': {
    ...theme.typography.subtitle2,
    color: theme.palette.text.secondary,
  },

  '& .fc .fc-list-event': {
    ...theme.typography.body2,
    '& .fc-list-event-time': {
      color: theme.palette.text.secondary,
    },
  },

  '& .fc .fc-list-table': {
    '& th, td': {
      borderColor: 'transparent',
    },
  },

  // Responsiveness
  [theme.breakpoints.down('sm')]: {
    '& .fc .fc-event .fc-event-main': {
      padding: '2px 4px',
    },
    '& .fc .fc-event .fc-event-main-frame': {
      fontSize: 12,
    },
  },
}));

export default CalendarStyle;
